import React, { FC, useMemo } from "react";
import { AttributeSelectorType, AttributeType, StringSubType } from "@evergis/api";
import { DatePicker, Icon, NumberInput, Textarea } from "@evergis/ui";
import { AttachmentsAttribute } from "./components/Attachments/AttachmentsAttribute";

import { FeatureAttributesContainer } from "../../styled";
import { AttributeValueSelector } from "./components/AttributeValueSelector";
import { CalculatedNumberInput } from "./components/CalculatedNumberInput";
import { ImageAttributes } from "../ImageAttributes/ImageAttributes";
import { CalculatedGeometryAttribute } from "./CalculatedGeometryAttribute";
import { FeatureCardCheckbox } from "../FeatureCardCheckbox/FeatureCardCheckbox";
import { FeatureAttribute, NUMBER_DECIMAL_SCALE } from "./FeatureAttributeField";
import { getDate, RawDateType } from "../../../../utils/date";
import { FeaturedDcExtendedGeometry } from "../../../../ducks/selectFeatures/types";
import { AttributesUpdate } from "../../../../hooks/attributes/useAttributes";
import { LayerInfo } from "../../../../interfaces/resourceInfo";
import { JSONAttributeValue } from "./types";

type EditableFeatureAttributesProps = {
  layerInfo: LayerInfo;
  attributes: FeatureAttribute[];
  updateAttribute: AttributesUpdate<FeatureAttribute>["updateAttribute"];
  storeAttributes: VoidFunction;
};

// eslint-disable-next-line max-lines-per-function
export const EditableFeatureAttributes: FC<EditableFeatureAttributesProps> = ({
  layerInfo,
  attributes,
  updateAttribute,
  storeAttributes,
}) => {
  const attributesWithSelector = useMemo(() => {
    const layerAttributes = layerInfo?.configuration.attributesConfiguration?.attributes || [];

    return layerAttributes
      ?.filter(({ attributeSelectorType }) => attributeSelectorType === AttributeSelectorType.SelectFromHandBook)
      .map(({ attributeName }) => attributeName);
  }, [layerInfo]);

  return (
    <FeatureAttributesContainer>
      {attributes.map(({ name, value, type, alias, readOnly, calculatedParameter, subType, stringFormat }) => {
        if (attributesWithSelector.includes(name)) {
          return (
            <AttributeValueSelector
              key={name}
              layer={layerInfo}
              name={name}
              alias={alias}
              value={value}
              attributeType={type}
              stringFormat={stringFormat}
              updateAttribute={updateAttribute}
              storeAttributes={storeAttributes}
            />
          );
        }

        if (type === AttributeType.String && subType === StringSubType.Image)
          return <ImageAttributes key={name} value={value as string} name={name} label={alias || name} isEdit />;

        if (type === AttributeType.Json && subType === StringSubType.Attachments)
          return (
            <AttachmentsAttribute
              key={name}
              value={value as JSONAttributeValue[]}
              label={alias || name}
              isEdit
              onChange={val => {
                updateAttribute(name, { value: val });
              }}
              storeAttributes={storeAttributes}
            />
          );

        switch (type) {
          case AttributeType.Boolean:
            return (
              <FeatureCardCheckbox
                key={name}
                checked={value as boolean}
                title={alias || name}
                onChange={() => updateAttribute(name, { value: !value })}
              />
            );

          case AttributeType.DateTime:
            return (
              <DatePicker
                zIndex={10}
                label={alias || name}
                key={name}
                width={"100%"}
                onChange={val => value !== val && updateAttribute(name, { value: val })}
                onBlur={storeAttributes}
                withHeader={false}
                value={getDate(value as RawDateType)}
                disabled={readOnly}
                prefix={<Icon kind="date" />}
                withTime
                clickable
              />
            );

          case AttributeType.Int64:
          case AttributeType.Double: {
            if (calculatedParameter) {
              return (
                <CalculatedNumberInput
                  parameter={calculatedParameter}
                  width={"100%"}
                  key={name}
                  label={alias || name}
                  value={value as number}
                  onChange={val => updateAttribute(name, { value: val })}
                  onBlur={storeAttributes}
                  numeralDecimalScale={NUMBER_DECIMAL_SCALE}
                  disabled={readOnly}
                />
              );
            }

            return (
              <NumberInput
                width={"100%"}
                key={name}
                label={alias || name}
                value={value as number}
                onChange={val => value !== val && updateAttribute(name, { value: val })}
                onBlur={storeAttributes}
                numeralDecimalScale={NUMBER_DECIMAL_SCALE}
                disabled={readOnly}
              />
            );
          }
          case AttributeType.Point:
          case AttributeType.Polyline:
          case AttributeType.MultiPolygon:
            return (
              <CalculatedGeometryAttribute
                key={name}
                value={value as FeaturedDcExtendedGeometry}
                label={alias || name}
                name={alias || name}
                isEdit
              />
            );

          default:
            return (
              <Textarea
                autoResize
                width={"100%"}
                key={name}
                label={alias || name}
                value={(value as string) || ""}
                onChange={e => updateAttribute(name, { value: e.target.value })}
                onBlur={storeAttributes}
                disabled={readOnly}
              />
            );
        }
      })}
    </FeatureAttributesContainer>
  );
};
